import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import React, { useEffect, useMemo, useState } from 'react';

import { calculatePercentage, getOccupancyBandColor } from '@app/containers/components/OccupancyRate';
import { Callout, Card, HTMLSelect, HTMLTable, Intent, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';
import DevText from '@components/Text';

import { TeamPeriodOccupancyResource, fetchTeamPeriodOccupancyList } from '../../data/api';
import { Controls, TableScroll, Toolbar } from '../../styled';
import {
    buildParticipantScoreMap,
    buildTeamScoreMap,
    collectMembers,
    filterMembersByRoles,
    filterMembersByTeam,
    getDefaultQuarterRange,
    getRoleUserIds,
    listMemberRoles,
    listQuarters,
    listSelectableQuarters,
    validateQuarterRange,
} from '../../utils/teamHealth';
import MemberIdentity, { getMemberRole } from '../MemberIdentity';
import RoleFilter, { RoleSelectionProps } from '../RoleFilter';
import WeeklyChart from '../WeeklyChart';

export interface QuarterlyOccupancyProps extends RoleSelectionProps {
    teams: TeamResource[];
}

type QuarterlyResult = { key: string; quarters: TeamPeriodOccupancyResource[][] } | { key: string; error: string };

const ALL_TEAMS = '';

const QuarterlyOccupancy: React.FC<QuarterlyOccupancyProps> = ({ teams, selectedRoles, onSelectedRolesChange }) => {
    const [options] = useState(() => listSelectableQuarters(new Date()));
    const [defaultRange] = useState(() => getDefaultQuarterRange(new Date()));
    const [teamId, setTeamId] = useState(ALL_TEAMS);
    const [fromValue, setFromValue] = useState(defaultRange.fromValue);
    const [toValue, setToValue] = useState(defaultRange.toValue);
    const [result, setResult] = useState<QuarterlyResult | null>(null);

    // Each quarter is requested on its own exact dates; an invalid range is not requested at all
    const validationMessage = validateQuarterRange(fromValue, toValue);
    const quarters = useMemo(() => listQuarters(fromValue, toValue), [fromValue, toValue]);

    const selectedTeams = useMemo(
        () => (teamId === ALL_TEAMS ? teams : teams.filter((team) => team.id === teamId)),
        [teams, teamId]
    );
    const requestKey = `${teamId}|${fromValue}|${toValue}`;

    useEffect(() => {
        if (validationMessage !== null) {
            return;
        }

        let isCancelled = false;
        const teamIds = selectedTeams.map((team) => team.id);
        setResult(null);
        fetchTeamPeriodOccupancyList(teamIds, quarters)
            .then((quarterItems) => !isCancelled && setResult({ key: requestKey, quarters: quarterItems }))
            .catch((e) => {
                console.error(e);
                if (!isCancelled) {
                    setResult({ key: requestKey, error: 'Occupancy for these quarters could not be loaded.' });
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [requestKey, validationMessage, selectedTeams, quarters]);

    const members = useMemo(() => collectMembers(teams), [teams]);
    const roles = useMemo(() => listMemberRoles(members), [members]);
    const roleUserIds = useMemo(() => getRoleUserIds(members, selectedRoles), [members, selectedRoles]);

    // Only render data that answers the team and quarters currently selected
    const current = result && result.key === requestKey ? result : null;
    const data = current && 'quarters' in current ? current : null;

    // Every quarter's bars follow the selected roles
    const quarterTeamScores = useMemo(
        () => (data?.quarters ?? []).map((items) => buildTeamScoreMap(items, roleUserIds)),
        [data, roleUserIds]
    );
    const quarterParticipantScores = useMemo(() => (data?.quarters ?? []).map(buildParticipantScoreMap), [data]);

    const renderQuarterOptions = () =>
        options.map((quarter) => (
            <option key={quarter.value} value={quarter.value}>
                {quarter.label}
            </option>
        ));

    const renderControls = () => (
        <Controls>
            <div>
                <label htmlFor="team-health-quarterly-team">Team</label>
                <HTMLSelect
                    id="team-health-quarterly-team"
                    value={teamId}
                    onChange={(e) => setTeamId(e.currentTarget.value)}
                >
                    <option value={ALL_TEAMS}>All Teams</option>
                    {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                            {team.title}
                        </option>
                    ))}
                </HTMLSelect>
            </div>
            <RoleFilter roles={roles} selectedRoles={selectedRoles} onSelectedRolesChange={onSelectedRolesChange} />
            <div>
                <label htmlFor="team-health-from-quarter">From Quarter</label>
                <HTMLSelect
                    id="team-health-from-quarter"
                    value={fromValue}
                    onChange={(e) => setFromValue(e.currentTarget.value)}
                >
                    {renderQuarterOptions()}
                </HTMLSelect>
            </div>
            <div>
                <label htmlFor="team-health-to-quarter">To Quarter</label>
                <HTMLSelect
                    id="team-health-to-quarter"
                    value={toValue}
                    onChange={(e) => setToValue(e.currentTarget.value)}
                >
                    {renderQuarterOptions()}
                </HTMLSelect>
            </div>
        </Controls>
    );

    const renderChartPanel = () => {
        if (validationMessage !== null) {
            return <Callout intent={Intent.WARNING}>{validationMessage}</Callout>;
        }

        if (current && 'error' in current) {
            return <Callout intent={Intent.DANGER}>{current.error}</Callout>;
        }

        if (!data) {
            return (
                <Flex justify="center">
                    <Spinner size={30} />
                </Flex>
            );
        }

        // Only teams with a bar in some quarter are charted
        const chartTeams = selectedTeams.filter((team) => quarterTeamScores.some((scores) => team.id in scores));
        if (selectedRoles.length > 0 && chartTeams.length === 0) {
            return <DevText muted>No occupancy for the selected roles.</DevText>;
        }

        return (
            <WeeklyChart
                teams={chartTeams.map((team) => ({ id: team.id, title: team.title, color: team.color }))}
                weeks={quarters}
                scores={quarterTeamScores}
                ariaLabel="Team occupancy by quarter"
            />
        );
    };

    const renderQuarterCell = (quarterIndex: number, userId: string) => {
        const score = quarterParticipantScores[quarterIndex]?.[userId];
        if (score === undefined) {
            return (
                <DevText inline muted>
                    {data ? 'Not scored' : '…'}
                </DevText>
            );
        }

        return (
            <DevText inline color={getOccupancyBandColor(score)}>
                {calculatePercentage(score)}%
            </DevText>
        );
    };

    const visibleMembers = filterMembersByRoles(
        filterMembersByTeam(members, teamId === ALL_TEAMS ? null : teamId),
        selectedRoles
    );

    return (
        <>
            <Toolbar>
                <DevText muted>
                    {validationMessage === null ?
                        `${quarters.length} quarter${quarters.length === 1 ? '' : 's'}: ${quarters[0].label} – ${quarters[quarters.length - 1].label} (UTC calendar quarters, weekdays through today)`
                    :   'Choose a valid range of quarters'}
                </DevText>
                {renderControls()}
            </Toolbar>

            <Card className="mb-2">{renderChartPanel()}</Card>

            {validationMessage === null && (
                <Card>
                    <TableScroll>
                        <HTMLTable striped>
                            <thead>
                                <tr>
                                    <th>Member</th>
                                    <th>Role</th>
                                    <th>Team</th>
                                    {quarters.map((quarter) => (
                                        <th key={quarter.value}>{quarter.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleMembers.map((member) => (
                                    <tr key={member.user.id}>
                                        <td>
                                            <MemberIdentity user={member.user} />
                                        </td>
                                        <td>{getMemberRole(member.user)}</td>
                                        <td>{member.teams.map((team) => team.title).join(', ')}</td>
                                        {quarters.map((quarter, quarterIndex) => (
                                            <td key={quarter.value}>
                                                {renderQuarterCell(quarterIndex, member.user.id)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {visibleMembers.length === 0 && (
                                    <tr>
                                        <td colSpan={3 + quarters.length}>
                                            <DevText muted>
                                                {selectedRoles.length > 0 ?
                                                    'No members match the selected roles.'
                                                :   'No active members.'}
                                            </DevText>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </HTMLTable>
                    </TableScroll>
                </Card>
            )}
        </>
    );
};

export default QuarterlyOccupancy;
